/**
 * Browse-by-state page. Renders only states that already have at least one
 * business (server filters those out via /api/businesses/states).
 *
 * Each tile links to /directory?state=XX so the existing directory filter
 * does the heavy lifting.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { API } from "@/App";

// Lookup map: 2-letter postal code → full state name (for display only).
// Anything not in this map shows the postal code unchanged.
const STATE_NAMES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas",
  CA: "California", CO: "Colorado", CT: "Connecticut", DE: "Delaware",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho",
  IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas",
  KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah",
  VT: "Vermont", VA: "Virginia", WA: "Washington", WV: "West Virginia",
  WI: "Wisconsin", WY: "Wyoming", DC: "District of Columbia",
};

export default function StatesPage() {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/businesses/states`)
      .then((r) => !cancelled && setStates(r.data.states || []))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // Mount-once fetch.
  }, []);

  return (
    <div className="min-h-screen bg-tbc-cream/30" data-testid="states-page">
      <Navbar />

      <section className="relative bg-tbc-navy text-white py-14 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.6),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tbc-gold text-xs uppercase tracking-[0.3em] font-medium mb-2">
            Find your state
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl">
            Browse by state
          </h1>
          <p className="mt-4 max-w-2xl text-white/70 text-lg">
            Discover Black-owned businesses listed where you live. Tap a state
            to see every business and product available there.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-6 h-6 inline mr-2 animate-spin" />
            Loading…
          </div>
        ) : states.length === 0 ? (
          <div className="text-center py-20 text-gray-500" data-testid="states-empty">
            <MapPin className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            No state data on file yet. Once businesses are added with a state, they&apos;ll appear here.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {states.map((s) => {
              const fullName = STATE_NAMES[s.state] || s.state;
              return (
                <Link
                  key={s.state}
                  to={`/directory?state=${encodeURIComponent(s.state)}`}
                  className="group relative bg-white border border-gray-200 hover:border-tbc-gold rounded-xl p-5 transition-all hover:shadow-md flex flex-col"
                  data-testid={`state-tile-${s.state}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="text-xs uppercase tracking-widest text-tbc-gold font-medium">
                      {s.state}
                    </span>
                    <span className="text-xs bg-tbc-cream rounded-full px-2 py-0.5 text-tbc-navy">
                      {s.count} {s.count === 1 ? "biz" : "businesses"}
                    </span>
                  </div>
                  <p className="mt-3 font-heading text-xl text-tbc-navy leading-tight">
                    {fullName}
                  </p>
                  <div className="mt-3 inline-flex items-center text-sm text-tbc-navy/70 group-hover:text-tbc-navy">
                    Browse <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
